// When React gives up on a page it empties it. An error no component catches,
// during hydration or after, unmounts the whole root and with it the HTML the
// server sent, leaving the reader, and Googlebot's renderer, a blank page.
// The commonest cause was a page cut short: Googlebot reads only the first
// 2 MB of HTML, a long transcript's React payload ran past that, and hydrating
// the cut payload failed with "Connection closed." Pages are smaller now, but
// a payload that arrives cut off, or any other uncaught error, would do the
// same.
//
// So keep the server's nodes before hydrating and put them back if React
// leaves the root empty. They come back as static HTML: readable, linkable
// and indexable, with nothing interactive.

interface HydrationRoot<N> {
  readonly childNodes: ArrayLike<N>;
  hasChildNodes(): boolean;
  appendChild(node: N): unknown;
}

/**
 * Remembers the root's server-rendered nodes. The returned function puts them
 * back if the root is empty when it is called.
 */
export function keepServerHtml<N>(root: HydrationRoot<N>): () => void {
  const serverNodes = Array.from(root.childNodes);
  return () => {
    if (root.hasChildNodes()) return;
    for (const node of serverNodes) root.appendChild(node);
  };
}
